import React, { useState, useCallback, useEffect } from 'react'
import { LuFilter } from 'react-icons/lu'
import {
  IoMdAddCircleOutline,
  IoIosArrowBack,
  IoIosArrowForward,
} from 'react-icons/io'
import { FaRegEdit } from 'react-icons/fa'
import { IoMailOutline } from 'react-icons/io5'

import { Panel, FlexContainer, Loader } from '@components/common'
import { Input, AutocompleteInput } from '@components/common/form'

import {
  IndexAccount,
  MarketplaceUser,
  LoadMarketplaceUsersParams,
  LoadClientReportingProgramsParams,
  ApiDinerProfile,
  ClientReportingProgram,
} from '@types'
import {
  CreateMarketplaceUserModal,
  EditMarketplaceUserModal,
} from '@containers/marketplaceUsers'

export type User = {
  marketplaceUser: MarketplaceUser
  dinerProfiles: ApiDinerProfile[]
  clientReportingPrograms: ClientReportingProgram[]
}

const getDpIds = (dinerProfiles: ApiDinerProfile[]) => {
  return dinerProfiles
    .map((dinerProfile) => dinerProfile.id || '')
    .filter((id) => !!id)
}

const newUser = (): User => {
  return {
    marketplaceUser: {
      id: undefined,
      name: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      isPasswordSet: false,
      isConfirmed: false,
      confirmedAt: null,
      confirmationSentAt: null,
      welcomeEmailSentAt: null,
      headquarterId: 0,
      marketplaceUserAccounts: [],
      contact: null,
    },
    dinerProfiles: [],
    clientReportingPrograms: [],
  } as User
}

export interface AccountSearchParams {
  search: string
  page?: number
  resultsPerPage?: number
  searchAllHqs?: boolean
}

interface MarketplaceUsersPageProps {
  loadAccounts: (params: AccountSearchParams) => Promise<IndexAccount[]>
  loadMarketplaceUsers: (
    params: LoadMarketplaceUsersParams,
  ) => Promise<MarketplaceUser[]>
  loadDinerProfiles: (params: {
    contactIds?: string[] | string
  }) => Promise<ApiDinerProfile[]>
  loadClientReportingPrograms: (
    params: LoadClientReportingProgramsParams,
  ) => Promise<ClientReportingProgram[]>
  sendWelcomeEmail: (params: { marketplaceUserId: string }) => Promise<void>
}

const MarketplaceUsersPage: React.FC<MarketplaceUsersPageProps> = ({
  loadAccounts,
  loadMarketplaceUsers: _loadMarketplaceUsers,
  loadDinerProfiles,
  loadClientReportingPrograms,
  sendWelcomeEmail,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [sortBy, setSortBy] = useState<'name' | 'email'>('name')
  const [client, setClient] = useState<IndexAccount | undefined>(undefined)
  const [page, setPage] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [marketplaceUsers, setMarketplaceUsers] = useState<User[]>([])

  const loadMarketplaceUsers = useCallback(
    async (page: number) => {
      setLoading(true)
      setPage(page)
      const users = await _loadMarketplaceUsers({
        search: searchQuery,
        clientId: client?.id,
        sortBy,
        page,
        resultsPerPage: 10,
      })
      const contactsIds = users
        .map((user) => (user.contact && user.contact.id) || '')
        .filter((id) => !!id)
      let dinerProfiles: ApiDinerProfile[] = []
      if (contactsIds.length > 0) {
        dinerProfiles = await loadDinerProfiles({ contactIds: contactsIds })
      }
      const dpIds = getDpIds(dinerProfiles)
      let reportingPrograms: ClientReportingProgram[] = []
      if (dpIds.length > 0) {
        reportingPrograms = await loadClientReportingPrograms({
          dinerProfileIds: dpIds,
        })
      }

      setMarketplaceUsers(
        users.map((user) => {
          const dps = dinerProfiles.filter(
            (dinerProfile) =>
              user.contact && dinerProfile.contactId === user.contact.id,
          )
          const dpIds = getDpIds(dps)
          const crps = reportingPrograms.filter((reportingProgram) =>
            reportingProgram.dinerProfileIds.some((dpId) =>
              dpIds.includes(dpId),
            ),
          )

          return {
            marketplaceUser: user,
            dinerProfiles: dps,
            clientReportingPrograms: crps,
          }
        }),
      )

      setLoading(false)
    },
    [
      _loadMarketplaceUsers,
      loadDinerProfiles,
      loadClientReportingPrograms,
      searchQuery,
      client,
      sortBy,
    ],
  )

  useEffect(() => {
    // initial load
    loadMarketplaceUsers(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSendWelcomeEmail = async (user: User) => {
    const { marketplaceUser } = user
    if (!marketplaceUser.id) {
      return
    }

    await sendWelcomeEmail({ marketplaceUserId: marketplaceUser.id })
    await loadMarketplaceUsers(page)
  }

  const renderUserRow = (user: User) => {
    const {
      marketplaceUser: {
        id,
        name,
        email,
        welcomeEmailSentAt,
        isConfirmed,
        isPasswordSet,
        marketplaceUserAccounts,
      },
      dinerProfiles,
      clientReportingPrograms,
    } = user

    return (
      <tr
        key={id}
        style={{
          padding: '10px',
          borderTop: '1px solid #e0e0e0',
          borderBottom: '1px solid #e0e0e0',
        }}
        className="hover:bg-gray-100"
      >
        <td className="py-4 px-2 align-middle">{name}</td>
        <td className="py-4 px-2 align-middle">{email}</td>
        <td className="py-4 px-2 text-center align-middle">
          {welcomeEmailSentAt ? (
            <span className="text-green-800 bg-green-200 px-2 py-1 rounded-lg font-semibold">
              Sent
            </span>
          ) : (
            <span
              className="text-orange-700 bg-white px-2 py-1 rounded-lg font-semibold"
              style={{ border: '1px solid #CCCCCC' }}
            >
              Not Sent
            </span>
          )}
        </td>
        <td className="py-4 px-2 text-center align-middle">
          {isConfirmed ? (
            <span className="text-green-800 bg-green-200 px-2 py-1 rounded-lg font-semibold">
              Verified
            </span>
          ) : (
            <span
              className="text-orange-700 bg-white px-2 py-1 rounded-lg font-semibold"
              style={{ border: '1px solid #CCCCCC' }}
            >
              Pending
            </span>
          )}
        </td>
        <td className="py-4 px-2 text-center align-middle">
          {isPasswordSet ? (
            <span className="text-green-800 bg-green-200 px-2 py-1 rounded-lg font-semibold">
              Complete
            </span>
          ) : (
            <span
              className="text-orange-700 bg-white px-2 py-1 rounded-lg font-semibold"
              style={{ border: '1px solid #CCCCCC' }}
            >
              Incomplete
            </span>
          )}
        </td>
        <td
          className={`py-4 px-2 ${
            marketplaceUserAccounts.length == 0 ? 'align-middle' : ''
          }`}
        >
          <div className="flex flex-row gap-2 flex-wrap">
            {marketplaceUserAccounts.length == 0 ? (
              <span className="text-2xl font-semibold">-</span>
            ) : (
              marketplaceUserAccounts.map((account) => (
                <span
                  className="bg-gray-200 px-2 py-1 rounded-lg font-semibold text-xs text-wrap"
                  key={account.id}
                >
                  {account.name}
                </span>
              ))
            )}
          </div>
        </td>
        <td
          className={`py-4 px-2 ${
            dinerProfiles.length == 0 ? 'align-middle' : ''
          }`}
        >
          <div className="flex flex-row gap-2 flex-wrap">
            {dinerProfiles.length == 0 ? (
              <span className="text-2xl font-semibold">-</span>
            ) : (
              dinerProfiles.map((dinerProfile) => (
                <span
                  className="bg-gray-200 px-2 py-1 rounded-lg font-semibold text-xs text-wrap"
                  key={dinerProfile.id}
                >
                  {dinerProfile.name}
                </span>
              ))
            )}
          </div>
        </td>
        <td
          className={`py-4 px-2 ${
            clientReportingPrograms.length == 0 ? 'align-middle' : ''
          }`}
        >
          <div className="flex flex-row gap-2 flex-wrap">
            {clientReportingPrograms.length == 0 ? (
              <span className="text-2xl font-semibold">-</span>
            ) : (
              clientReportingPrograms.map((clientReportingProgram) => (
                <span
                  className="bg-gray-200 px-2 py-1 rounded-lg font-semibold text-xs text-wrap"
                  key={clientReportingProgram.id}
                >
                  {clientReportingProgram.name}
                </span>
              ))
            )}
          </div>
        </td>
        <td className="py-4 px-2 align-middle">
          <div className="flex flex-col gap-2 items-start">
            <button
              className="bg-transparent font-semibold text-xs items-center flex"
              onClick={() => {
                setSelectedUser(user)
                setModalMode('edit')
              }}
            >
              <FaRegEdit size={20} /> Edit
            </button>
            {!isConfirmed && (
              <button
                className="bg-transparent font-semibold text-xs items-center flex"
                onClick={() => onSendWelcomeEmail(user)}
              >
                <IoMailOutline size={20} /> Send Welcome Email
              </button>
            )}
          </div>
        </td>
      </tr>
    )
  }

  const renderModals = () => {
    if (selectedUser) {
      if (modalMode === 'create') {
        return (
          <CreateMarketplaceUserModal
            hideModal={() => setSelectedUser(null)}
            loadAccounts={loadAccounts}
            refreshMarketplaceUsers={loadMarketplaceUsers}
          />
        )
      }
      if (modalMode === 'edit') {
        return (
          <EditMarketplaceUserModal
            hideModal={() => setSelectedUser(null)}
            user={selectedUser}
            sendWelcomeEmail={sendWelcomeEmail}
            refreshMarketplaceUsers={loadMarketplaceUsers}
            loadAccounts={loadAccounts}
          />
        )
      }
    }

    return null
  }

  return (
    <>
      <FlexContainer
        flexDirection="column"
        width="100%"
        maxHeight="1000px"
        alignItems="center"
        padding="50px 0"
      >
        <Panel maxWidth="80vw" heading="Search Marketplace Client Admin Users">
          <div className="flex flex-row gap-4">
            <Input
              label="Search"
              placeholder="Search by name, email..."
              width="50%"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div style={{ width: '50%' }}>
              <AutocompleteInput
                label="Client Profile Name"
                placeholder="Filter by client profile name"
                value={client?.name || ''}
                displayAttribute="name"
                loaderFunction={(search: AccountSearchParams) =>
                  loadAccounts({ ...search, searchAllHqs: true })
                }
                onSelect={(account: IndexAccount) => setClient(account)}
                onClear={() => setClient(undefined)}
                includePagination={true}
              />
            </div>
          </div>
          <button
            className="flex flex-row gap-2 items-center bg-black text-white px-4 py-2 rounded-md font-semibold"
            onClick={async () => {
              await loadMarketplaceUsers(1)
            }}
          >
            <LuFilter color="white" size={20} /> Apply Filters
          </button>
          <div className="flex w-full flex-row justify-between my-5 items-center">
            <div>
              <span className="text-lg font-semibold mr-3">SORT</span>
              <select
                className="border border-gray-300 rounded-md px-2 py-1"
                onChange={(e) => setSortBy(e.target.value as 'name' | 'email')}
              >
                <option value="name">Name</option>
                <option value="email">Email</option>
              </select>
            </div>
            <button
              className="flex flex-row gap-2 items-center bg-black text-white px-4 py-2 rounded-md font-semibold"
              onClick={() => {
                setSelectedUser(newUser())
                setModalMode('create')
              }}
            >
              <IoMdAddCircleOutline color="white" size={20} /> Create User
            </button>
          </div>
          <div className="flex flex-row gap-2">
            {page > 1 && (
              <button
                className="bg-transparent font-semibold text-xs items-center flex"
                onClick={async () => {
                  await loadMarketplaceUsers(page - 1)
                }}
              >
                <IoIosArrowBack size={20} /> Previous
              </button>
            )}
            <span className="text-xs font-semibold"> Page: {page} </span>
            {marketplaceUsers.length === 10 && (
              <button
                className="bg-transparent font-semibold text-xs items-center flex"
                onClick={async () => {
                  await loadMarketplaceUsers(page + 1)
                }}
              >
                Next <IoIosArrowForward size={20} />
              </button>
            )}
          </div>
          <div
            className="w-full"
            style={{ maxHeight: '500px', overflowY: 'auto' }}
          >
            {loading ? (
              <Loader />
            ) : (
              <table>
                <thead className="sticky top-0 bg-white">
                  <tr>
                    <th
                      style={{ width: '8%' }}
                      className="font-semibold text-gray-600 text-md text-left"
                    >
                      Name
                    </th>
                    <th
                      style={{ width: '12%' }}
                      className="font-semibold text-gray-600 text-md text-left"
                    >
                      Email
                    </th>
                    <th
                      style={{ width: '10%' }}
                      className="font-semibold text-gray-600 text-md text-left"
                    >
                      Welcome Email Sent
                    </th>
                    <th
                      style={{ width: '10%' }}
                      className="font-semibold text-gray-600 text-md text-left"
                    >
                      Email Verified
                    </th>
                    <th
                      style={{ width: '10%' }}
                      className="font-semibold text-gray-600 text-md text-left"
                    >
                      Account Setup
                    </th>
                    <th
                      style={{ width: '12%' }}
                      className="font-semibold text-gray-600 text-md text-left"
                    >
                      Client Profiles
                    </th>
                    <th
                      style={{ width: '12%' }}
                      className="font-semibold text-gray-600 text-md text-left"
                    >
                      Diner Profiles
                    </th>
                    <th
                      style={{ width: '12%' }}
                      className="font-semibold text-gray-600 text-md text-left"
                    >
                      Reporting Profiles
                    </th>
                    <th
                      style={{ width: '14%' }}
                      className="font-semibold text-gray-600 text-md text-left"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {marketplaceUsers.map((user) => renderUserRow(user))}
                </tbody>
              </table>
            )}
          </div>
        </Panel>
      </FlexContainer>
      {renderModals()}
    </>
  )
}

export default MarketplaceUsersPage
